import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CreateMessageGenSessionRequest, MessageGenSession, MessageGenState } from '@trinity/chat';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useLoadChatInstance } from '@trinity/chat-components/components/Chat.History/hooks/index.js';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier.js';
import {
  type MessageGenerationCreateSession,
  type MessageGenerationTableActions,
  MessageGenerationCreateSessionContext,
  MessageGenerationDispatchContext,
  MessageGenerationLoadSessionContext,
  MessageGenerationResetSessionContext,
  MessageGenerationStateContext,
  MessageGenerationTableActionsContext,
  MessageGenerationTableActionsValueContext,
} from './Context';

interface Props {
  children: ReactNode;
}

export const MessageGenerationStateContainer = ({ children }: Props): ReactNode => {
  const apiClient = useChatApiClient();
  const queryClient = useQueryClient();
  const loadChatInstance = useLoadChatInstance();
  const brandIdentifier = useActiveBrandIdentifier();

  const [session, setSession] = useState<MessageGenSession | null>(null);
  const [tableActions, setTableActions] = useState<MessageGenerationTableActions | null>(null);

  const { mutate: createMutation } = useMutation({
    mutationFn: (data: CreateMessageGenSessionRequest) => apiClient.messageGeneration.createSession(data),
    onSuccess: (data) => {
      console.log('Created message generation session:', data);
      setSession(data);
      queryClient.invalidateQueries({ queryKey: ['message-gen-sessions'] });
    },
  });

  const loadSession = useCallback(
    (messageGenSessionId: number) => {
      apiClient.messageGeneration.getSession(messageGenSessionId).then((data) => {
        setSession(data);
        // Load the chat instance if it exists
        if (data.chatInstanceIdentifier) {
          loadChatInstance(data.chatInstanceIdentifier);
        }
      });
    },
    [apiClient, loadChatInstance]
  );

  const createSession: MessageGenerationCreateSession = useCallback(
    ({ brandIdentifier, sessionId, initialState }) => {
      console.log('Creating message generation session', sessionId, brandIdentifier, initialState);
      createMutation({ sessionId, brandIdentifier, initialState });
    },
    [createMutation]
  );

  const dispatch = useCallback((state: MessageGenState) => {
    console.log('Updating message generation session state:', state);
    // @ts-ignore
    setSession((prev) => (prev ? { ...prev, state } : prev));
  }, []);

  const resetSession = useCallback(async () => {
    if (!session || !brandIdentifier) return;


    try {
      // Create a brand new session with the same configuration
      const newSession = await apiClient.messageGeneration.createSession({
        sessionId: session.sessionId || undefined,
        brandIdentifier,
        initialState: {
          segmentIds: session.state.initialSegmentIds,
        },
      });

      setSession(newSession);
      queryClient.invalidateQueries({ queryKey: ['message-gen-sessions'] });

      // Load the new chat instance
      if (newSession.chatInstanceIdentifier) {
        loadChatInstance(newSession.chatInstanceIdentifier);
      }
    } catch (error) {
      console.error('Failed to reset session:', error);
    }
  }, [session, brandIdentifier, apiClient, loadChatInstance]);

  return (
    <MessageGenerationStateContext.Provider value={session}>
      <MessageGenerationDispatchContext.Provider value={dispatch}>
        <MessageGenerationCreateSessionContext.Provider value={createSession}>
          <MessageGenerationLoadSessionContext.Provider value={loadSession}>
            <MessageGenerationResetSessionContext.Provider value={resetSession}>
              <MessageGenerationTableActionsContext.Provider value={setTableActions}>
                <MessageGenerationTableActionsValueContext.Provider value={tableActions}>
                  {children}
                </MessageGenerationTableActionsValueContext.Provider>
              </MessageGenerationTableActionsContext.Provider>
            </MessageGenerationResetSessionContext.Provider>
          </MessageGenerationLoadSessionContext.Provider>
        </MessageGenerationCreateSessionContext.Provider>
      </MessageGenerationDispatchContext.Provider>
    </MessageGenerationStateContext.Provider>
  );
};
